import clsx from "clsx";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import moment from "moment/moment";
import { PATIENT_CONTEXT_TYPE } from "../../../../../util/patientContextUtil";
import AmbHandoverButton from "../../../forms/clinicianForm/ambulanceHendover/ambHandoverButton";
import { TimerCountUp } from "../../../../../controls/agyle/timer";
import React, { useEffect, useRef, useState } from "react";
import { useMainScreenButtonStyles } from "../../../../../components/forms/mainScreenButtonStyles";
import { useDispatch, useSelector } from "react-redux";
import { ambulanceCodes } from "../../../../../store/reducers/dicts";
import { getConfig } from "../../../../../util/configUtil";
import { useTranslation } from "react-i18next";
import _ from "lodash";
import { calculateArrivalDate } from "../../../../../util/patientUtil";
import { UtilsMomentJS } from "../../../../../store/utilsMoment";
import { useMainScreenStyles } from "../../mainScreenStyles";
import OffWardButton from "../../../forms/offWard/offWardButton";
import ReferralReviewButton from "../../../forms/referralReview/referralReviewButton";
import { getPatientFlagValue } from "../../util/patientFlagsUtil";
import { hasPermission, Permissions } from "../../../../../store/reducers/user";
import SettingsIcon from "@mui/icons-material/Settings";
import { Tooltip } from "@material-ui/core";
import IconButton from "@mui/material/IconButton";
import {
    ctxCounterVisitsED,
  ctxCounterVisitsIP,
  ctxPatientPastActivity,
  ctxSplitScreen,
  openSplitScreenWithRegistry,
} from "../../../../../store/reducers/patientContext";

const RenderActivityButtonInfo = ({ patient, patientType }) => {
  const classesButton = useMainScreenButtonStyles();
  const classes = useMainScreenStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();

  // Selectors
  const ambulance = useSelector(ambulanceCodes);
  const splitScreen = useSelector(ctxSplitScreen);
  const edVisits = useSelector(ctxCounterVisitsED);
  const ipVisits = useSelector(ctxCounterVisitsIP);
  const patientPastActivity = useSelector(ctxPatientPastActivity);

  // Configs
  const defaultDateTimeFormat = getConfig("defaultDateTimeFormat");
  const ecdsDateFormat = getConfig("ecdsDateFormat");
  const referralReviewForm = getConfig("referralReviewForm", false);
  const registrationEnabled = getConfig("admitPatient.registration", false);

  // Permissions
  const canAdmit = dispatch(hasPermission(Permissions.AGYLE_CAN_ADMIT_PATIENT));

  // Others
  const hasSRFlag = getPatientFlagValue(patient, "S&R");

  // Container width
  const container = useRef(null);
  const [isSmall, setIsSmall] = useState(false);

  useEffect(() => {
    const adjustWidth = () => {
      if (container.current) {
        const width = container.current.offsetWidth;
        setIsSmall(width < 400);
      }
    };

    adjustWidth();
    const resizeListener = () => adjustWidth();
    window.addEventListener("resize", resizeListener);

    return () => {
      window.removeEventListener("resize", resizeListener);
    };
  }, [splitScreen, patient, container]);

  // Timer configuration
  const getPatientTime = (format) => {
    // If patient have arrival mode code for ambulance then calculate time form ambulance handover
    if (
      !_.isEmpty(ambulance) &&
      ambulance.includes(patient?.admission?.arrivalModeCode)
    ) {
      return format
        ? moment(calculateArrivalDate(patient)).format(defaultDateTimeFormat)
        : calculateArrivalDate(patient);
    } else {
      return format
        ? moment(patient?.admission?.startDate).format(defaultDateTimeFormat)
        : moment(patient?.admission?.startDate);
    }
  };

  const checkStatus = () => {
    let dischargeDate;
    if (patient?.studies?.discharge) {
      const date = moment(
        patient?.studies?.discharge?.departureDate,
        ecdsDateFormat
      );
      const time = moment(patient?.studies?.discharge?.departureTime)
        .set({
          year: date.get("year"),
          month: date.get("month"),
          day: date.get("day"),
        })
        .toISOString();
      if (!moment(patient?.studies?.discharge?.departureTime).isValid()) {
        dischargeDate = UtilsMomentJS.getLocalDateFromUTCECDSFormat(
          patient?.studies?.discharge?.departureDate,
          patient?.studies?.discharge?.departureTime
        );
      } else {
        dischargeDate = time;
      }
    }
    return dischargeDate;
  };

  const handleEditActivity = () => {
    dispatch(openSplitScreenWithRegistry(patient, false));
  };

  const getAttendanceDetails = () => {
    return (
      <div style={{ gridRow: "2 span" }} ref={container}>
        <div className={clsx(classes.paperDarker, classes.cellReadOnlyElement)}>
          <Grid
            container
            direction="row"
            justifyContent="space-between"
            alignItems="top"
            style={{
              borderRadius: 8,
              overflow: "hidden",
            }}
          >
            <Grid item xs={12} className={classesButton.buttonHeaderFilled}>
              <Typography variant="subtitle1" fontWeight="bolder">
                {t("Activity Information")}
              </Typography>

              {(registrationEnabled && canAdmit && !patientPastActivity) && (
                <Tooltip title={t("Update Patient Activity Information")}>
                  <IconButton
                    size="small"
                    onClick={handleEditActivity}
                  >
                    <SettingsIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Grid>

            <Grid item xs={12} className={clsx(classesButton.seenDlg)}>
              <Grid
                container
                direction="row"
                className={clsx(classes.paddingX, "label-set")}
              >
                <Grid item xs={isSmall ? 12 : 6} sx={{ pt: 0.5 }}>
                  <div className={classes.cellReadOnlyLabel}>
                    {t("Arrival Date")}
                  </div>

                  <div className={classes.cellReadOnlyValue}>
                    {moment(patient?.admission?.startDate).format(
                      defaultDateTimeFormat
                    )}
                  </div>
                </Grid>

                {patientType !== PATIENT_CONTEXT_TYPE.EDAU && (
                  <Grid item xs={isSmall ? 12 : 6} sx={{ pt: 0.5 }}>
                    <div className={classes.cellReadOnlyLabel}>
                      {t("Derived Arrival Date")}
                    </div>
                    <div className={classes.cellReadOnlyValue}>
                      {getPatientTime(true)}
                    </div>
                  </Grid>
                )}

                {!ambulance?.includes(patient?.admission?.arrivalModeCode) && (
                  <Grid item xs={12} sx={{ mt: 5 }} />
                )}

                {ambulance?.includes(patient?.admission?.arrivalModeCode) && (
                  <Grid item xs={12} sx={{ py: 0.5 }}>
                    <AmbHandoverButton
                      patient={patient}
                      value={patient?.ambulanceHandover}
                    />
                  </Grid>
                )}

                {referralReviewForm && hasSRFlag && (
                  <Grid item xs={12} sx={{ py: 0.5 }}>
                    <ReferralReviewButton />
                  </Grid>
                )}

                <Grid item xs={isSmall ? 12 : 6}>
                  <div className={classes.singleGridDisplayItem}>
                    <Typography
                      variant="subtitle1"
                      fontWeight="bolder"
                      fontSize={14}
                      sx={{ mb: 1 }}
                    >
                      {t("Visits in the last 12 months")}
                    </Typography>

                    <div className={classes.visitBoxContainer}>
                      <div className={classes.visitBox}>
                        ED: {edVisits ?? 0}
                      </div>
                      <div className={classes.visitBox}>
                        IP: {ipVisits ?? 0}
                      </div>
                    </div>
                  </div>
                </Grid>

                <Grid item xs={isSmall ? 12 : 6} pb={2}>
                  <div className={classes.singleGridDisplayItem}>
                    <Typography
                      variant="subtitle1"
                      fontWeight="bolder"
                      fontSize={14}
                      sx={{ mb: 1 }}
                    >
                      {t("Time in ED")}
                    </Typography>

                    <div className={classes.timerDynamic}>
                      <TimerCountUp
                        renderWithBG
                        limit={240}
                        upperLimit={-1}
                        countDownSince={
                          patient.admission?.status === "DISCHARGE_REQUESTED" ||
                          patient.admission?.status === "TRANSFER_REQUESTED"
                            ? checkStatus()
                            : false
                        }
                        time={getPatientTime()}
                        limitMessage={t("4hr Target Breached!")}
                      />
                    </div>
                  </div>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </div>
      </div>
    );
  };

  const getAdmissionDetails = () => {
    return (
      <div style={{ gridRow: "2 span" }}>
        <div className={clsx(classes.paperDarker, classes.cellReadOnlyElement)}>
          <Grid
            container
            direction="row"
            justifyContent="space-between"
            alignItems="top"
            style={{
              borderRadius: 8,
              overflow: "hidden",
            }}
          >
            <Grid item xs={12} className={classesButton.buttonHeaderFilled}>
              <Typography variant="subtitle1" fontWeight="bolder">
                {t("Episodic Information")}
              </Typography>
            </Grid>

            <Grid item xs={12} className={clsx(classesButton.seenDlg)}>
              <Grid
                container
                direction="row"
                className={clsx(classes.paddingX, "label-set")}
                style={{ backgroundColor: "unset" }}
              >
                <Grid item xs={6} sx={{ pt: 0.5 }}>
                  <div className={classes.cellReadOnlyLabelIP}>
                    {t("Admission Date:")}
                  </div>

                  <div className={classes.cellReadOnlyValue}>
                    {moment(patient.admission.startDate).format(
                      defaultDateTimeFormat
                    )}
                  </div>
                </Grid>

                <Grid
                  item
                  xs={12}
                  sx={{ pb: 1, pt: 8 }}
                  className={classes.divRow}
                  style={{ justifyContent: "space-between" }}
                >
                  <div className={classes.singleGridDisplayItem}>
                    <div className={classes.cellReadOnlyLabelIP}>
                      {t("Location: ")}
                    </div>
                    <div className={classes.cellReadOnlyValue}>
                      {patient.bedDescription} / {patient.areaDescription} /{" "}
                      {patient.wardId}
                    </div>
                  </div>

                  <div>
                    <OffWardButton patient={patient} />
                  </div>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </div>
      </div>
    );
  };

  const renderActivityInformation = () => {
    switch (patientType) {
      case PATIENT_CONTEXT_TYPE.EDAU:
      case PATIENT_CONTEXT_TYPE.TEMPORARY:
      case PATIENT_CONTEXT_TYPE.ED:
      case PATIENT_CONTEXT_TYPE.SDEC:
      case PATIENT_CONTEXT_TYPE.FAST_REGISTRY:
        return getAttendanceDetails();
      case PATIENT_CONTEXT_TYPE.INPATIENT:
        return getAdmissionDetails();
      default:
        return null;
    }
  };

  if (!patient?.admission) return null;
  return renderActivityInformation();
};

export default RenderActivityButtonInfo;
